import React, { Component } from 'react';
import { Flex, Heading, Text, Button, Card, Tag, Lookup } from 'core/components';
import { VirtualizedTable } from 'core/components/table';
import CustomApplicationCollection from 'app/stores/customApplications/CustomApplicationCollection';
import ASNRenderer from 'app/components/asn/ASNRenderer';

// Row height constants
const BASE_ROW_HEIGHT = 50;
const ITEM_HEIGHT = 28;
const ROW_PADDING = 8;

const parseAsnField = (value) => {
  if (!value) {
    return [];
  }
  return value
    .toString()
    .split(',')
    .map((a) => a.trim())
    .filter(Boolean);
};

const clampedTextRenderer = ({ value, model }) => {
  const clamp = Math.max(2, model.get('protocol').length);
  return (
    <Text
      ellipsis
      overflow="hidden"
      whiteSpace="normal"
      style={{
        display: '-webkit-box',
        WebkitLineClamp: `${clamp}`,
        WebkitBoxOrient: 'vertical'
      }}
    >
      {value.join(', ')}
    </Text>
  );
};

class CustomApplicationsToolResult extends Component {
  state = {
    collection: new CustomApplicationCollection()
  };

  componentDidMount() {
    const { collection } = this.state;
    const { tool } = this.props;
    const { raw_result = [] } = tool;
    collection.add(raw_result);
  }

  get columns() {
    return [
      {
        name: 'name',
        label: 'Application Name',
        renderer: ({ value }) => <Text fontWeight="bold">{value}</Text>
      },
      {
        name: 'protocol',
        label: 'Protocol',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        ellipsis: false,
        renderer: ({ value }) =>
          value &&
          value.map((protocol) => (
            <Tag key={protocol} minimal my="2px">
              <Lookup lookup="protocols" value={protocol} />
            </Tag>
          ))
      },
      {
        name: 'port',
        label: 'Port Number',
        ellipsis: false,
        renderer: clampedTextRenderer
      },
      {
        name: 'ip_range',
        label: 'IP Address',
        ellipsis: false,
        renderer: clampedTextRenderer
      },
      {
        name: 'asn',
        label: 'ASN',
        ellipsis: false,
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        renderer: ({ value }) => {
          const asns = parseAsnField(value);
          if (asns.length === 0) {
            return null;
          }

          return (
            <Flex flexDirection="column" gap="4px">
              {asns.map((asn) => (
                <ASNRenderer key={asn} asn={asn} showLinks showAsnAsTag />
              ))}
            </Flex>
          );
        }
      },
      {
        name: 'description',
        label: 'Description',
        ellipsis: true,
        renderer: ({ value }) => <Text ellipsis>{value}</Text>
      }
    ];
  }

  getRowHeight = ({ model }) => {
    const protocols = model.get('protocol') || [];
    const asns = parseAsnField(model.get('asn'));
    const maxItems = Math.max(protocols.length, asns.length);
    return Math.max(maxItems * ITEM_HEIGHT + ROW_PADDING, BASE_ROW_HEIGHT);
  };

  render() {
    const { collection } = this.state;
    const { tool, onCloseToolResult } = this.props;

    return (
      <Card
        p={0}
        className="tool-result-custom-applications"
        display="flex"
        flexDirection="column"
        gap={2}
        minHeight={600}
        height="100%"
        overflow="hidden"
      >
        <Flex alignItems="center" justifyContent="space-between" p={2} pb={0}>
          <Heading level={5} m={0} flex={1}>
            {tool.title || 'Custom Applications Result'}
          </Heading>
          <Flex alignItems="center" gap={1}>
            <Button text="Close" small icon="cross" onClick={() => onCloseToolResult(tool)} color="muted" minimal />
          </Flex>
        </Flex>
        <Flex flexDirection="column" px={1} pb={1} flex={1}>
          <VirtualizedTable
            collection={collection}
            loading={false}
            selectOnRowClick={false}
            rowHeight={this.getRowHeight}
            columns={this.columns}
            flexed
          />
        </Flex>
      </Card>
    );
  }
}

export default CustomApplicationsToolResult;
